import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AuthProvider, useAuth } from './lib/auth';
import { StepUpHost } from './components/StepUp';
import { EngineProvider } from './engine/api/context';
import { ThemeProvider } from './engine/design/theme';
import { ToastProvider } from './engine/design/primitives/Toast';
import { ConfirmProvider } from './engine/design/primitives/Overlay';
import { HotkeyProvider } from './engine/layout/shortcuts';
import { InspectorProvider } from './engine/views/Inspector';
import { Shell } from './engine/shell';
import { moduleRoutes } from './engine/router';
import { modules } from './modules';
import { LoginPage } from './pages/LoginPage';
import { LandingPage } from './pages/LandingPage';
import { OnboardingGate } from './pages/OnboardingGate';
import { AcceptInvitePage } from './pages/AcceptInvitePage';
import { OAuthConsentPage } from './modules/platform/OAuthConsentPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { QuotePage } from './public/quote/QuotePage';
import { BookingPage } from './public/booking/BookingPage';
import { StatusPage } from './public/status/StatusPage';
import { RatePage } from './public/rate/RatePage';
import { PortalApp } from './portal/PortalApp';
import { AdminApp } from './modules/admin/AdminApp';
import { ApiError } from './engine/api/client';

// Retry a failed read once — but never a client error or a 501 (the operation is not available
// for this organisation, e.g. a NovaPSA-only panel on a Halo-connected tenant): those cannot succeed.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failures, err) => failures < 1 && !(err instanceof ApiError && ((err.status >= 400 && err.status < 500) || err.status === 501)),
      refetchOnWindowFocus: false,
    },
  },
});

// Signed-in routes come from modules[] (engine screens + declared custom screens) and
// render inside the AppShell. Auth / onboarding routes stay outside the shell.
function Routing() {
  const { session, loading, client, principal, signOut, me } = useAuth();
  if (loading) {
    return (
      <div className="hf-loading" style={{ minHeight: '100vh' }}>
        <span className="hf-spinner" /> Loading…
      </div>
    );
  }
  return (
    <EngineProvider client={client} modules={modules} principal={principal} signOut={signOut}>
      <InspectorProvider>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/login/halo/callback" element={<LoginPage />} />
          {/* Set-new-password: reached via a recovery code (from login) or a recovery link. */}
          <Route path="/reset" element={<ResetPasswordPage />} />
          {/* Invite acceptance handles the signed-out case itself (a new invitee has no tenant). */}
          <Route path="/invite" element={<AcceptInvitePage />} />
          {/* OAuth consent for an app connecting over MCP (docs/mcp.md); handles signed-out itself. */}
          <Route path="/oauth/consent" element={<OAuthConsentPage />} />
          {/* Public quote acceptance: the URL token is the credential; no session, no shell. */}
          <Route path="/q/:token" element={<QuotePage />} />
          <Route path="/book/:token" element={<BookingPage />} />
          <Route path="/status/:slug" element={<StatusPage />} />
          <Route path="/rate/:token" element={<RatePage />} />
          {/* Customer portal: its own API group (/portal/v1), auth gate and shell (docs/modules/portal.md). */}
          <Route path="/portal/*" element={<PortalApp />} />
          {session ? (
            <>
              {/* Platform-admin console: its own shell + /v1/admin engine, gated on the
                  platform role. Outside the tenant OnboardingGate so a platform_owner with
                  no tenant can still reach it. */}
              <Route path="/admin/*" element={<AdminApp />} />
              <Route
                element={
                  <OnboardingGate>
                    <Shell brand="NovaPSA" brandShort="Nova" />
                  </OnboardingGate>
                }
              >
                {moduleRoutes(modules)}
              </Route>
            </>
          ) : (
            /* Logged-out visitors get the public landing page at the root. */
            <Route path="/" element={<LandingPage />} />
          )}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        {/* The second factor: asked for when the API needs a step-up, or opened from the account menu. */}
        {session && <StepUpHost enforced={Boolean(me?.security?.mfa_required && me.security.aal !== 'aal2')} />}
      </InspectorProvider>
    </EngineProvider>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <ToastProvider>
          <ConfirmProvider>
            <HotkeyProvider>
              <AuthProvider>
                <BrowserRouter>
                  <Routing />
                </BrowserRouter>
              </AuthProvider>
            </HotkeyProvider>
          </ConfirmProvider>
        </ToastProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
